import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["searchInput", "categoryList", "categoryItem", "createOption", "dropdown", "hiddenInput"]

  connect() {
    this.selectedIndex = -1
    this.isDropdownOpen = false
    // Set up click outside handler
    this.boundHandleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener('click', this.boundHandleClickOutside)
  }

  disconnect() {
    document.removeEventListener('click', this.boundHandleClickOutside)
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target) && this.isDropdownOpen) {
      this.hideDropdown()
    }
  }

  focusSearchInput() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.focus()
      this.showDropdown()
    }
  }

  showDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.remove('hidden')
      this.isDropdownOpen = true
      this.selectedIndex = -1
      this.updateSelection()
    }
  }

  hideDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add('hidden')
      this.isDropdownOpen = false
      this.selectedIndex = -1
    }
  }

  search() {
    const searchTerm = this.searchInputTarget.value.toLowerCase().trim()
    
    // Show dropdown when typing
    this.showDropdown()
    
    this.categoryItemTargets.forEach(item => {
      const categoryName = item.dataset.categoryName
      const isVisible = categoryName.includes(searchTerm)
      
      if (isVisible) {
        item.classList.remove('hidden')
      } else {
        item.classList.add('hidden')
      }
    })

    // Reset selection when search changes
    this.selectedIndex = -1
    this.updateSelection()

    // Show create option and no results message
    this.updateCreateOption(searchTerm)
    this.showNoResultsMessage(searchTerm)
  }

  showNoResultsMessage(searchTerm) {
    // Remove existing no results message
    const existingMessage = this.categoryListTarget.querySelector('.no-results-message')
    if (existingMessage) {
      existingMessage.remove()
    }

    // Only show message if there's a search term and no visible items AND no create option visible
    const hasVisibleItems = this.getVisibleItems().length > 0
    const hasCreateOption = this.hasCreateOptionTarget && !this.createOptionTarget.classList.contains('hidden')
    
    if (searchTerm && !hasVisibleItems && !hasCreateOption) {
      const noResultsMessage = document.createElement('li')
      noResultsMessage.className = 'no-results-message px-4 py-2 text-gray-500 dark:text-gray-400 italic text-center'
      noResultsMessage.textContent = `No categories found for "${searchTerm}"`
      
      // Simply append to the list
      this.categoryListTarget.appendChild(noResultsMessage)
    }
  }

  getVisibleItems() {
    return this.categoryItemTargets.filter(item => !item.classList.contains('hidden'))
  }

  updateCreateOption(searchTerm) {
    if (!this.hasCreateOptionTarget) return

    const hasResults = this.getVisibleItems().length > 0
    const hasQuery = searchTerm.length > 0

    if (!hasResults && hasQuery) {
      // Show create option and update text with original capitalization
      this.createOptionTarget.classList.remove('hidden')
      const createText = this.createOptionTarget.querySelector('[data-create-text]')
      if (createText) {
        createText.textContent = this.searchInputTarget.value.trim()
      }
    } else {
      // Hide create option
      this.createOptionTarget.classList.add('hidden')
    }
  }

  getNavigableItems() {
    const visibleItems = this.getVisibleItems()
    // Include create option if visible
    if (this.hasCreateOptionTarget && !this.createOptionTarget.classList.contains('hidden')) {
      return [...visibleItems, { isCreateOption: true, element: this.createOptionTarget }]
    }
    return visibleItems
  }

  createCategory() {
    const searchTerm = this.searchInputTarget.value.trim()
    if (!searchTerm) return

    // Hide dropdown first
    const dropdown = document.getElementById('timerDropdown')
    if (dropdown) {
      dropdown.classList.add('hidden')
    }

    // Find the new-category controller on the page
    const newCategoryElement = document.querySelector('[data-controller*="new-category"]')
    if (newCategoryElement) {
      const newCategoryController = this.application.getControllerForElementAndIdentifier(newCategoryElement, 'new-category')
      if (newCategoryController && newCategoryController.showModalWithName) {
        newCategoryController.showModalWithName(searchTerm)
      } else {
        console.error('New category controller or showModalWithName method not found')
      }
    } else {
      console.error('New category element not found')
    }
  }

  handleKeydown(event) {
    const navigableItems = this.getNavigableItems()
    
    if (event.key === 'Escape') {
      // Hide dropdown and clear search
      this.hideDropdown()
      this.searchInputTarget.value = ''
      this.search()
      event.preventDefault()
    } else if (event.key === 'ArrowDown') {
      // Show dropdown if not shown and navigate down
      if (!this.isDropdownOpen) {
        this.showDropdown()
      }
      if (navigableItems.length > 0) {
        this.selectedIndex = Math.min(this.selectedIndex + 1, navigableItems.length - 1)
        this.updateSelection()
      }
      event.preventDefault()
    } else if (event.key === 'ArrowUp') {
      // Navigate up through navigable items
      if (navigableItems.length > 0) {
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1)
        this.updateSelection()
      }
      event.preventDefault()
    } else if (event.key === 'Enter') {
      // Activate selected item
      if (this.selectedIndex >= 0 && navigableItems[this.selectedIndex]) {
        const selectedItem = navigableItems[this.selectedIndex]
        if (selectedItem.isCreateOption) {
          this.createCategory()
        } else {
          this.selectCategory({ currentTarget: selectedItem })
        }
      }
      event.preventDefault()
    }
  }

  updateSelection() {
    const navigableItems = this.getNavigableItems()
    
    // Remove highlight from all items
    this.categoryItemTargets.forEach(item => {
      item.classList.remove('bg-blue-50', 'dark:bg-blue-900/20')
    })
    
    // Remove highlight from create option
    if (this.hasCreateOptionTarget) {
      this.createOptionTarget.classList.remove('bg-blue-50', 'dark:bg-blue-900/20')
    }
    
    // Highlight selected item
    if (this.selectedIndex >= 0 && navigableItems[this.selectedIndex]) {
      const selectedItem = navigableItems[this.selectedIndex]
      if (selectedItem.isCreateOption) {
        this.createOptionTarget.classList.add('bg-blue-50', 'dark:bg-blue-900/20')
      } else {
        selectedItem.classList.add('bg-blue-50', 'dark:bg-blue-900/20')
      }
      
      // Scroll into view if needed
      const elementToScroll = selectedItem.isCreateOption ? this.createOptionTarget : selectedItem
      elementToScroll.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      })
    }
  }

  clearSearch() {
    this.searchInputTarget.value = ''
    this.selectedIndex = -1
    this.search()
    this.searchInputTarget.focus()
    // Clear hidden input
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = ''
    }
  }

  selectCategory(event) {
    const categoryItem = event.currentTarget
    const categoryId = categoryItem.dataset.categoryId
    
    // Try to find the category name from different possible elements
    let categoryName
    const spanElement = categoryItem.querySelector('span')
    const divElement = categoryItem.querySelector('div.font-medium')
    
    if (spanElement) {
      categoryName = spanElement.textContent
    } else if (divElement) {
      categoryName = divElement.textContent
    } else {
      // Fallback: use the data attribute or text content
      categoryName = categoryItem.dataset.categoryName || categoryItem.textContent.trim()
    }
    
    const categoryColor = categoryItem.dataset.categoryColor

    // Update hidden input
    if (this.hasHiddenInputTarget) {
      this.hiddenInputTarget.value = categoryId
    }

    // Update search input with selected category name
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.value = categoryName
    }


    // Hide dropdown
    this.hideDropdown()
  }
}